import { Button } from '@components/common';
import { Pencil, Trash2 } from 'lucide-react';
import React from 'react';
import { InventoryStockLocationRecord } from '@/renderer/types/inventory';

function StockLocationStatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        isActive
          ? 'bg-emerald-50 text-emerald-700'
          : 'bg-gray-100 text-gray-600'
      }`}
    >
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function formatLocationType(value: InventoryStockLocationRecord['type']) {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function StockLocationsTable({
  locations,
  onEdit,
  onDelete,
  onSetDefault,
  isSettingDefault = false,
}: {
  locations: InventoryStockLocationRecord[];
  onEdit: (location: InventoryStockLocationRecord) => void;
  onDelete: (location: InventoryStockLocationRecord) => void;
  onSetDefault?: (location: InventoryStockLocationRecord) => void;
  isSettingDefault?: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="max-h-[34rem] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Location
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Type
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Description
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {locations.map((location) => (
              <tr key={location.id} className="border-t border-gray-100">
                <td className="px-5 py-4 font-medium text-gray-900">
                  <div className="flex items-center gap-2">
                    {location.name}
                    {location.is_default ? (
                      <span className="inline-flex rounded-full bg-pos-primary/10 px-2 py-0.5 text-xs font-semibold text-pos-primary">
                        Default
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="px-5 py-4 text-gray-600">
                  {formatLocationType(location.type)}
                </td>
                <td className="px-5 py-4 text-gray-600">
                  {location.description || 'No description'}
                </td>
                <td className="px-5 py-4">
                  <StockLocationStatusBadge isActive={location.is_active} />
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    {onSetDefault && !location.is_default ? (
                      <Button
                        variant="text"
                        size="sm"
                        disabled={isSettingDefault}
                        onClick={() => onSetDefault(location)}
                      >
                        Set as default
                      </Button>
                    ) : null}
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() => onEdit(location)}
                    >
                      <Pencil size={14} />
                      Edit
                    </Button>
                    <Button
                      variant="text"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => onDelete(location)}
                    >
                      <Trash2 size={14} />
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
